import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const WACampaignAdmin = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState('30d');

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/wa/admin/campaigns?status=${filterStatus}&range=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, dateRange]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const statusColors = {
    draft: { bg: '#f3f4f6', text: '#374151' },
    scheduled: { bg: '#dbeafe', text: '#1e40af' },
    queued: { bg: '#fef3c7', text: '#92400e' },
    running: { bg: '#fef3c7', text: '#92400e' },
    paused: { bg: '#f3e8ff', text: '#6b21a8' },
    completed: { bg: '#dcfce7', text: '#166534' },
    failed: { bg: '#fee2e2', text: '#991b1b' },
    cancelled: { bg: '#f3f4f6', text: '#374151' },
  };

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  const filteredCampaigns = campaigns.filter(c =>
    !searchTerm ||
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(c.user_id).includes(searchTerm)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
            <Icons.Megaphone className="w-5 h-5 text-white" />
          </div>
          Campaign Management (Admin)
        </h1>
        <p className="text-sm text-gray-500 mt-1">Monitor and manage all WhatsApp push campaigns across all users</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: 'Total Campaigns', value: campaigns.length || '0', icon: 'Megaphone', color: '#25D366' },
          { label: 'Running', value: campaigns.filter(c => c.status === 'running').length || '0', icon: 'Play', color: '#128C7E' },
          { label: 'Scheduled', value: campaigns.filter(c => c.status === 'scheduled').length || '0', icon: 'Clock', color: '#34B7F1' },
          { label: 'Completed', value: campaigns.filter(c => c.status === 'completed').length || '0', icon: 'CheckCircle', color: '#075E54' },
          { label: 'Failed', value: campaigns.filter(c => c.status === 'failed').length || '0', icon: 'AlertTriangle', color: '#ef4444' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}14`, color: stat.color }}>
                {getIcon(stat.icon, 18)}
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-[11px] text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns or user ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
            />
          </div>
          <div className="flex rounded-xl border border-gray-200 overflow-hidden">
            {['all', 'running', 'scheduled', 'completed', 'failed'].map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className="px-3 py-2 text-xs font-medium transition-all"
                style={{
                  background: filterStatus === s ? '#25D366' : 'white',
                  color: filterStatus === s ? 'white' : '#6b7280',
                }}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex rounded-xl border border-gray-200 overflow-hidden">
            {['7d', '30d', '90d'].map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className="px-3 py-2 text-xs font-medium transition-all"
                style={{
                  background: dateRange === range ? '#25D366' : 'white',
                  color: dateRange === range ? 'white' : '#6b7280',
                }}
              >
                {range}
              </button>
            ))}
          </div>
          <button onClick={fetchCampaigns} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-500">
            <Icons.RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Campaign List */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
            <p className="text-sm text-gray-500">Loading campaigns...</p>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
              <Icons.Megaphone className="w-8 h-8" style={{ color: '#25D366' }} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No Campaigns Found</h3>
            <p className="text-sm text-gray-500">No WhatsApp push campaigns match your filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium">Campaign</th>
                  <th className="text-left px-4 py-3 font-medium">User</th>
                  <th className="text-left px-4 py-3 font-medium">Template</th>
                  <th className="text-center px-4 py-3 font-medium">Recipients</th>
                  <th className="text-center px-4 py-3 font-medium">Sent</th>
                  <th className="text-center px-4 py-3 font-medium">Delivered</th>
                  <th className="text-center px-4 py-3 font-medium">Read</th>
                  <th className="text-center px-4 py-3 font-medium">Failed</th>
                  <th className="text-center px-4 py-3 font-medium">Status</th>
                  <th className="text-center px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map((campaign) => (
                  <tr key={campaign.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{campaign.name}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {campaign.scheduled_at ? `Scheduled: ${campaign.scheduled_at}` : `Created: ${campaign.created_at}`}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      <span className="px-2 py-0.5 rounded bg-gray-100">User #{campaign.user_id}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{campaign.template_id || 'N/A'}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700">{(campaign.total_recipients || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700">{(campaign.total_sent || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-green-600">{(campaign.total_delivered || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-center text-sm text-blue-600">{(campaign.total_read || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-center text-sm text-red-600">{(campaign.total_failed || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{
                        background: statusColors[campaign.status]?.bg || '#f3f4f6',
                        color: statusColors[campaign.status]?.text || '#374151',
                      }}>
                        {(campaign.status || 'unknown').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="View Details">
                          <Icons.Eye className="w-4 h-4" />
                        </button>
                        {campaign.status === 'running' && (
                          <button className="p-1.5 rounded-lg hover:bg-yellow-50 text-gray-400 hover:text-yellow-600" title="Pause">
                            <Icons.Pause className="w-4 h-4" />
                          </button>
                        )}
                        {campaign.status === 'scheduled' && (
                          <button className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500" title="Cancel">
                            <Icons.XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WACampaignAdmin;
